import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Tag, Button, Space, Tooltip, Modal, Tabs, Input, Spin, Dropdown, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, DeleteOutlined, EyeOutlined, FileTextOutlined, MailOutlined, MoreOutlined } from '@ant-design/icons';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { TableSearch } from 'modules/table';
import { DatePicker as FormDatePicker, Input as FormInput, Select as FormSelect } from 'modules/form';
import config from 'config';
import PDFViewer from './PDFViewer';
import { useDocumentProcessing } from './DocumentProcessingContext';
import type { DocumentData } from '../types';
import {
    AssessmentReportRepliesList,
    fetchAssessmentReportReplies,
    type AssessmentReportReply,
} from './AssessmentReportReplies';
import {
    fetchPortalDocumentResult,
    fetchPortalDocumentResultJob,
    generatePortalDocumentResult,
    reviewPortalDocumentResult,
    type PortalDocumentGenerateResponse,
    type PortalDocumentGenerateJobResponse,
    type PortalDocumentReviewResponse,
    type PortalDocumentResultResponse,
    type ResultStatus,
    type ReviewStatus,
} from '../services/portalDocumentsResultService';

interface PortalDocumentCustomer {
    id: string;
    email: string;
    fullName: string;
    account: {
        id: string;
        slug: string;
        emailDomain: string;
    };
}

type AssessmentReportStatus = 'submitted' | 'reply_received' | 'completed' | null;

interface PortalDocument {
    id: string;
    queueType: string;
    originalDocumentUrl: string;
    gcpStatus: 'pending' | 'processing' | 'processed' | 'failed';
    analysisDocumentUrl: string | null;
    analysisResults: Record<string, unknown> | null;
    analysisText: string | null;
    status: 'uploaded' | 'processing' | 'analysed' | 'completed';
    assessmentReportStatus: AssessmentReportStatus;
    caseNumber: string | null;
    createdAt: string;
    updatedAt: string;
    customer: PortalDocumentCustomer;
    // Rossum's extracted content (deficiency items, fabrikNumber, etc.) once matched
    // by rossumFileName — null while extraction is still pending/unmatched.
    extraction: DocumentData | null;
}

interface LandingPagePagination {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

interface PortalDocumentsResponse {
    documents: PortalDocument[];
    pagination: LandingPagePagination;
    extractions: DocumentData[];
    extractionsPagination: LandingPagePagination;
}

type LandingPageRow =
    | { source: 'portal'; key: string; data: PortalDocument }
    | { source: 'extraction'; key: string; data: DocumentData };

interface LandingPageDocumentsTabProps {
    isActive?: boolean;
}

type DisplayStatus = 'processing' | 'analysed' | 'completed';

// Matches EXTERNAL_QUEUE_TYPE in the backend's landingPageAssessmentReportService.ts —
// the queueType stamped on every CustomerDocument created via the assessment-report
// upload/submit flow, regardless of whether a case has been submitted yet.
const ASSESSMENT_REPORT_QUEUE_TYPE = 'landing-pages-assessment-report';

function filenameFromUrl(url: string): string {
    try {
        const parts = new URL(url).pathname.split('/');
        return decodeURIComponent(parts[parts.length - 1]) || url;
    } catch {
        return url;
    }
}

function getDisplayStatus(document: PortalDocument): DisplayStatus {
    if (document.status === 'uploaded') {
        return 'processing';
    }
    if (document.status === 'processing') {
        return 'processing';
    }
    if (document.status === 'analysed') {
        return 'analysed';
    }
    return 'completed';
}

// The label a row actually shows is derived from several fields (source,
// assessmentReportStatus, extraction presence), not just `status` — this is
// the single source of truth for that derivation, used by both the status
// column's render and the status filter so the two never disagree.
type RowStatusKey =
    | 'extraction_submitted'
    | 'extraction_pending'
    | 'case_submitted'
    | 'case_reply_received'
    | 'case_completed'
    | 'extracted'
    | 'extracting'
    | 'analysed'
    | 'completed';

const ROW_STATUS_META: Record<RowStatusKey, { color: string; id: string; defaultMessage?: string }> = {
    extraction_submitted: { color: 'green', id: 'document.processing.landing.status.submitted', defaultMessage: 'Submitted' },
    extraction_pending: { color: 'orange', id: 'guest.documents.gcpStatus.pending', defaultMessage: 'Pending' },
    case_submitted: { color: 'default', id: 'guest.documents.caseStatus.submitted' },
    case_reply_received: { color: 'gold', id: 'guest.documents.caseStatus.reply_received' },
    case_completed: { color: 'green', id: 'guest.documents.caseStatus.completed' },
    extracted: { color: 'purple', id: 'document.processing.landing.status.extracted', defaultMessage: 'Extracted' },
    extracting: { color: 'blue', id: 'document.processing.landing.status.extracting', defaultMessage: 'Extracting' },
    analysed: { color: 'cyan', id: 'guest.documents.status.analysed' },
    completed: { color: 'green', id: 'guest.documents.status.completed' },
};

function getRowStatusKey(row: LandingPageRow): RowStatusKey {
    if (row.source === 'extraction') {
        return row.data.submittedToAI ? 'extraction_submitted' : 'extraction_pending';
    }

    const document = row.data;
    if (document.assessmentReportStatus === 'submitted') {
        return 'case_submitted';
    }
    if (document.assessmentReportStatus === 'reply_received') {
        return 'case_reply_received';
    }
    if (document.assessmentReportStatus === 'completed') {
        return 'case_completed';
    }

    const status = getDisplayStatus(document);
    if (status === 'processing') {
        return document.extraction ? 'extracted' : 'extracting';
    }
    if (status === 'analysed') {
        return 'analysed';
    }
    return 'completed';
}

// Lower = higher priority (needs attention sooner) — extraction rows that
// haven't been submitted yet outrank submitted ones; a reply having arrived
// outranks everything else since it's the one state that needs a human to
// act; portal rows without a case rank by how far along their own workflow is.
function statusPriority(row: LandingPageRow): number {
    if (row.source === 'extraction') {
        return row.data.submittedToAI ? 1 : 0;
    }

    if (row.data.assessmentReportStatus === 'reply_received') return 0;
    if (row.data.assessmentReportStatus === 'submitted') return 1;
    if (row.data.assessmentReportStatus === 'completed') return 3;

    const status = getDisplayStatus(row.data);
    if (status === 'processing') return 1;
    if (status === 'analysed') return 2;
    return 3;
}

function getHtmlContent(document: PortalDocument): string | null {
    const html = document.analysisResults?.html;
    return typeof html === 'string' && html.trim() ? html : null;
}

function getResultPlaceholderTone(document: PortalDocument): {
    badgeColor: string;
    badgeBackground: string;
    badgeBorder: string;
} {
    const status = getDisplayStatus(document);

    if (status === 'completed') {
        return {
            badgeColor: '#166534',
            badgeBackground: '#dcfce7',
            badgeBorder: '#bbf7d0',
        };
    }

    if (status === 'analysed') {
        return {
            badgeColor: '#155e75',
            badgeBackground: '#cffafe',
            badgeBorder: '#a5f3fc',
        };
    }

    return {
        badgeColor: '#9a3412',
        badgeBackground: '#ffedd5',
        badgeBorder: '#fed7aa',
    };
}

type ChatMessage = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
};

type StoredDraft = {
    chatInput: string;
    chatMessages: ChatMessage[];
};

const PAGE_SIZE = 20;
const { TextArea } = Input;
const DRAFT_STORAGE_PREFIX = 'landing-page-documents-draft';
const QUEUE_TYPE_OPTIONS = ['invoice', 'contract', 'quotation', 'other'];
// Filter options, in the order statuses tend to appear as a document moves
// through the pipeline. `case_completed` and `completed` render the same
// "Completed" label (see ROW_STATUS_META), so they're offered as one option.
const STATUS_FILTER_OPTIONS: { value: string; keys: RowStatusKey[] }[] = [
    { value: 'extraction_pending', keys: ['extraction_pending'] },
    { value: 'extraction_submitted', keys: ['extraction_submitted'] },
    { value: 'extracting', keys: ['extracting'] },
    { value: 'extracted', keys: ['extracted'] },
    { value: 'analysed', keys: ['analysed'] },
    { value: 'case_submitted', keys: ['case_submitted'] },
    { value: 'case_reply_received', keys: ['case_reply_received'] },
    { value: 'completed', keys: ['completed', 'case_completed'] },
];

function rowMatchesStatusFilter(row: LandingPageRow, filterValue: string): boolean {
    const option = STATUS_FILTER_OPTIONS.find(({ value }) => value === filterValue);
    if (!option) {
        return true;
    }
    return option.keys.includes(getRowStatusKey(row));
}

function getDraftStorageKey(documentId: string): string {
    return `${DRAFT_STORAGE_PREFIX}:${documentId}`;
}

function readStoredDraft(documentId: string): StoredDraft | null {
    try {
        const raw = window.localStorage.getItem(getDraftStorageKey(documentId));
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as StoredDraft;
        if (!parsed || typeof parsed.chatInput !== 'string' || !Array.isArray(parsed.chatMessages)) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

function writeStoredDraft(documentId: string, draft: StoredDraft) {
    try {
        window.localStorage.setItem(getDraftStorageKey(documentId), JSON.stringify(draft));
    } catch {}
}

function clearStoredDraft(documentId: string) {
    try {
        window.localStorage.removeItem(getDraftStorageKey(documentId));
    } catch {}
}

const LandingPageDocumentsTab: React.FC<LandingPageDocumentsTabProps> = ({ isActive = false }) => {
    const intl = useIntl();
    const token = useSelector<any, string | undefined>(
        state => state?.petrus?.entities?.tokens?.accessToken?.token,
    );

    const { getPdfUrl, setDetailModalVisible, landingPageRefreshToken } = useDocumentProcessing();

    const isMountedRef = useRef(true);
    const [rows, setRows] = useState<LandingPageRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [filterOptions, setFilterOptions] = useState<{ [key: string]: string }>({});
    const [appliedFilters, setAppliedFilters] = useState<{ [key: string]: string }>({});
    const [selectedDocument, setSelectedDocument] = useState<PortalDocument | null>(null);
    const [resultHtml, setResultHtml] = useState<string>('');
    const [chatInput, setChatInput] = useState('');
    const [isGeneratingHtml, setIsGeneratingHtml] = useState(false);
    const [isReviewingResult, setIsReviewingResult] = useState(false);
    const [isDeletingDocument, setIsDeletingDocument] = useState(false);
    const [documentPendingDelete, setDocumentPendingDelete] = useState<LandingPageRow | null>(null);
    const [documentPendingResolve, setDocumentPendingResolve] = useState<LandingPageRow | null>(null);
    const [isResolvingDocument, setIsResolvingDocument] = useState(false);
    const [repliesDocument, setRepliesDocument] = useState<PortalDocument | null>(null);
    const [replies, setReplies] = useState<AssessmentReportReply[]>([]);
    const [repliesLoading, setRepliesLoading] = useState(false);
    const [repliesError, setRepliesError] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [resultStatus, setResultStatus] = useState<ResultStatus>('not_started');
    const [reviewStatus, setReviewStatus] = useState<ReviewStatus>('pending');
    const [resultVersion, setResultVersion] = useState<number | null>(null);
    const pollTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (pollTimeoutRef.current) {
                window.clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    const commonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            'Accept-Language': intl.locale,
            'Content-Type': 'application/json',
        }),
        [intl.locale, token],
    );

    const fetchDocuments = useCallback(async (currentPage: number) => {
        if (!token) {
            if (isMountedRef.current) {
                setRows([]);
                setTotal(0);
            }
            return;
        }

        if (isMountedRef.current) {
            setLoading(true);
        }

        try {
            const offset = (currentPage - 1) * PAGE_SIZE;
            const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
            if (appliedFilters.queueType) {
                params.append('queueType', appliedFilters.queueType);
            }

            // `status` is filtered client-side below, against the same derived label the
            // status column shows — the raw `status` DB column the backend can filter on
            // doesn't line up with what's actually displayed (see getRowStatusKey).
            const filterKeys = Object.keys(appliedFilters).filter(
                key => key !== 'queueType' && key !== 'status' && appliedFilters[key],
            );
            if (filterKeys.length > 0) {
                const filters: Record<string, string> = {};
                filterKeys.forEach(key => {
                    filters[key] = appliedFilters[key];
                });
                params.append('filters', JSON.stringify(filters));
            }

            const url = `${config.api.base}${config.api.portalDocuments}?${params.toString()}`;
            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Accept-Language': intl.locale,
                },
            });

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const data: PortalDocumentsResponse = await res.json();
            if (!isMountedRef.current) {
                return;
            }

            const portalRows: LandingPageRow[] = (data.documents || []).map(doc => ({
                source: 'portal',
                key: `portal-${doc.id}`,
                data: doc,
            }));
            const extractionRows: LandingPageRow[] = (data.extractions || []).map(doc => ({
                source: 'extraction',
                key: `extraction-${doc.jsonId}`,
                data: doc,
            }));
            const rowDate = (row: LandingPageRow) =>
                new Date(row.source === 'portal' ? row.data.createdAt : row.data.uploadedAt).getTime();

            let merged = [...portalRows, ...extractionRows].sort((a, b) => rowDate(b) - rowDate(a));
            const total = (data.pagination?.total || 0) + (data.extractionsPagination?.total || 0);

            if (appliedFilters.status) {
                // Filtered against this page's rows only — the backend paginates before
                // this filter runs, so a page can show fewer than PAGE_SIZE rows and the
                // total below stays the unfiltered count (an exact filtered total would
                // require the backend to filter by the same derived label).
                merged = merged.filter(row => rowMatchesStatusFilter(row, appliedFilters.status));
            }

            setRows(merged);
            setTotal(total);
        } catch {
            if (!isMountedRef.current) {
                return;
            }
            setRows([]);
            setTotal(0);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [token, intl.locale, appliedFilters]);

    useEffect(() => {
        if (!isActive) {
            return;
        }
        fetchDocuments(page);
    }, [fetchDocuments, isActive, page, landingPageRefreshToken]);

    const handleFilterChange = useCallback((changed: { [key: string]: string }) => {
        setFilterOptions(prev => ({ ...prev, ...changed }));
    }, []);

    const handleFilterSubmit = useCallback((event: React.FormEvent) => {
        event.preventDefault();
        setPage(1);
        setAppliedFilters(filterOptions);
    }, [filterOptions]);

    const handleFilterReset = useCallback(() => {
        setFilterOptions({});
        setPage(1);
        setAppliedFilters({});
    }, []);

    const openDocument = useCallback((document: PortalDocument) => {
        setRows(prevRows =>
            prevRows.map(row =>
                row.source === 'portal' && row.data.id === document.id && row.data.status === 'uploaded'
                    ? { ...row, data: { ...row.data, status: 'processing' } }
                    : row,
            ),
        );

        setSelectedDocument(current =>
            current?.id === document.id && current.status === 'uploaded'
                ? { ...current, status: 'processing' }
                : {
                    ...document,
                    status: document.status === 'uploaded' ? 'processing' : document.status,
                },
        );
    }, []);

    const handleViewExtraction = useCallback(async (extraction: DocumentData) => {
        try {
            await getPdfUrl(extraction);
            setDetailModalVisible(true);
        } catch {
            message.error(intl.formatMessage({
                id: 'document.processing.error.pdf.not.found',
                defaultMessage: 'PDF file not found or could not be loaded',
            }));
        }
    }, [getPdfUrl, intl, setDetailModalVisible]);

    // Portal rows normally open the chat/HTML-result modal, but once a document
    // has been matched to its extraction (Rossum finished OCR), there's real
    // deficiency-item content to review — open the same DynamicForm screen
    // extraction rows use instead, carrying the real landingPageDocId along so
    // "Submit to AI" can use it in place of gcpDocumentPath/customerEmail.
    const openRow = useCallback((row: LandingPageRow) => {
        if (row.source === 'extraction') {
            handleViewExtraction(row.data);
            return;
        }

        if (row.data.extraction) {
            handleViewExtraction({
                ...row.data.extraction,
                landingPageDocId: row.data.id,
                assessmentReportStatus: row.data.assessmentReportStatus,
            });
            return;
        }

        openDocument(row.data);
    }, [handleViewExtraction, openDocument]);

    const closeDocumentModal = useCallback(() => {
        if (pollTimeoutRef.current) {
            window.clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        setSelectedDocument(null);
    }, []);

    const fetchCurrentResult = useCallback(async (documentId: string) => {
        if (!token) {
            return;
        }

        const data: PortalDocumentResultResponse = await fetchPortalDocumentResult(documentId, commonHeaders);
        if (!isMountedRef.current) {
            return null;
        }

        setResultStatus(data.resultStatus);
        setReviewStatus(data.reviewStatus);
        setResultVersion(data.version);
        setResultHtml(data.html || '');

        return data;
    }, [commonHeaders, token]);

    const pollGenerateJob = useCallback(async (documentId: string, jobId: string) => {
        if (!token) {
            return;
        }

        const data: PortalDocumentGenerateJobResponse = await fetchPortalDocumentResultJob(
            documentId,
            jobId,
            commonHeaders,
        );
        if (!isMountedRef.current) {
            return;
        }

        if (data.status === 'processing') {
            setResultStatus('processing');
            pollTimeoutRef.current = window.setTimeout(() => {
                pollGenerateJob(documentId, jobId);
            }, 2000);
            return;
        }

        if (data.status === 'completed') {
            setResultStatus('ready');
            setResultHtml(data.html || '');
            setResultVersion(data.version);
            setIsGeneratingHtml(false);
            fetchDocuments(page);
            setChatMessages(prev => [
                ...prev,
                {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: intl.formatMessage({
                        id: 'document.processing.landing.chat.successMessage',
                        defaultMessage: 'Preview updated, your changes are applied.',
                    }),
                },
            ]);
            return;
        }

        setResultStatus('failed');
        setIsGeneratingHtml(false);
        fetchDocuments(page);
        message.error(data.errorMessage || intl.formatMessage({
            id: 'document.processing.landing.chat.generateFailed',
            defaultMessage: 'Failed to generate updated HTML.',
        }));
    }, [commonHeaders, fetchDocuments, intl, page, token]);

    const handleMarkResultReviewed = useCallback(async () => {
        if (!selectedDocument || !token) {
            return;
        }

        setIsReviewingResult(true);
        try {
            const data: PortalDocumentReviewResponse = await reviewPortalDocumentResult(
                selectedDocument.id,
                commonHeaders,
                'reviewed',
                true,
            );
            if (!isMountedRef.current) {
                return;
            }

            setRows(prevRows =>
                prevRows.map(row =>
                    row.source === 'portal' && row.data.id === selectedDocument.id
                        ? { ...row, data: { ...row.data, status: data.status } }
                        : row,
                ),
            );

            setReviewStatus(data.reviewStatus);
            clearStoredDraft(selectedDocument.id);
            setIsReviewingResult(false);
            message.success(intl.formatMessage({
                id: data.emailNotification?.sent
                    ? 'document.processing.landing.chat.reviewEmailSent'
                    : 'document.processing.landing.chat.reviewedMessage',
                defaultMessage: data.emailNotification?.sent
                    ? 'The result was marked as reviewed and the email was sent.'
                    : 'The result has been marked as reviewed.',
            }));
            fetchDocuments(page);
            closeDocumentModal();
        } catch {
            if (isMountedRef.current) {
                setIsReviewingResult(false);
                message.error(intl.formatMessage({
                    id: 'document.processing.landing.chat.reviewFailed',
                    defaultMessage: 'Failed to mark the result as reviewed.',
                }));
            }
        }
    }, [closeDocumentModal, commonHeaders, fetchDocuments, intl, page, selectedDocument, token]);

    const handleDeleteDocument = useCallback(async () => {
        if (!documentPendingDelete || !token) {
            return;
        }

        setIsDeletingDocument(true);
        try {
            const deleteUrl = documentPendingDelete.source === 'portal'
                ? `${config.api.base}${config.api.portalDocuments}/${documentPendingDelete.data.id}`
                : `${config.api.base}${config.api.documentProcessing}/${documentPendingDelete.data.jsonId}`;

            const response = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: commonHeaders,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            if (!isMountedRef.current) {
                return;
            }

            if (documentPendingDelete.source === 'portal' && selectedDocument?.id === documentPendingDelete.data.id) {
                closeDocumentModal();
            }
            setDocumentPendingDelete(null);
            fetchDocuments(page);
        } catch {
            if (isMountedRef.current) {
                message.error(intl.formatMessage({
                    id: 'document.processing.error.delete.failed',
                    defaultMessage: 'Failed to delete document',
                }));
            }
        } finally {
            if (isMountedRef.current) {
                setIsDeletingDocument(false);
            }
        }
    }, [closeDocumentModal, commonHeaders, documentPendingDelete, fetchDocuments, intl, page, selectedDocument, token]);

    const handleResolveDocument = useCallback(async () => {
        if (!documentPendingResolve || documentPendingResolve.source !== 'portal' || !token) {
            return;
        }

        setIsResolvingDocument(true);
        try {
            const response = await fetch(
                `${config.api.base}${config.api.landingPageAssessmentReports}/${documentPendingResolve.data.id}/status`,
                {
                    method: 'PATCH',
                    headers: commonHeaders,
                    body: JSON.stringify({ status: 'completed' }),
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            if (!isMountedRef.current) {
                return;
            }

            message.success(intl.formatMessage({
                id: 'document.processing.landing.resolve.success',
                defaultMessage: 'Marked as resolved — the customer has been notified.',
            }));
            setDocumentPendingResolve(null);
            fetchDocuments(page);
        } catch {
            if (isMountedRef.current) {
                message.error(intl.formatMessage({
                    id: 'document.processing.landing.resolve.failed',
                    defaultMessage: 'Failed to mark as resolved',
                }));
            }
        } finally {
            if (isMountedRef.current) {
                setIsResolvingDocument(false);
            }
        }
    }, [commonHeaders, documentPendingResolve, fetchDocuments, intl, page, token]);

    const fetchReplies = useCallback((documentId: string) => {
        if (!token) return;

        setRepliesLoading(true);
        setRepliesError(false);
        fetchAssessmentReportReplies(documentId, commonHeaders)
            .then(setReplies)
            .catch(() => setRepliesError(true))
            .finally(() => setRepliesLoading(false));
    }, [commonHeaders, token]);

    const openReplies = useCallback((document: PortalDocument) => {
        setRepliesDocument(document);
        fetchReplies(document.id);
    }, [fetchReplies]);

    const closeReplies = useCallback(() => {
        setRepliesDocument(null);
        setReplies([]);
        setRepliesError(false);
    }, []);

    useEffect(() => {
        if (!selectedDocument) {
            if (pollTimeoutRef.current) {
                window.clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
            setResultHtml('');
            setChatInput('');
            setChatMessages([]);
            setIsGeneratingHtml(false);
            setIsReviewingResult(false);
            setResultStatus('not_started');
            setReviewStatus('pending');
            setResultVersion(null);
            setReplies([]);
            setRepliesError(false);
            return;
        }

        setReplies([]);
        setRepliesError(false);
        if (selectedDocument.assessmentReportStatus) {
            fetchReplies(selectedDocument.id);
        }

        const storedDraft = readStoredDraft(selectedDocument.id);
        setChatInput(storedDraft?.chatInput || '');
        setIsReviewingResult(false);
        setChatMessages(
            storedDraft?.chatMessages?.length
                ? storedDraft.chatMessages
                : [
                    {
                        id: `assistant-${selectedDocument.id}`,
                        role: 'assistant',
                        content: intl.formatMessage({
                            id: 'document.processing.landing.chat.initialMessage',
                            defaultMessage: 'Describe the changes you want and I will update the preview.',
                        }),
                    },
                ],
        );
        setResultHtml('');
        setResultStatus('not_started');
        setReviewStatus('pending');
        setResultVersion(null);

        fetchCurrentResult(selectedDocument.id).catch(() => {
            if (isMountedRef.current) {
                setResultStatus('failed');
            }
        });
    }, [fetchCurrentResult, fetchReplies, intl, selectedDocument]);

    useEffect(() => {
        if (!selectedDocument) {
            return;
        }

        writeStoredDraft(selectedDocument.id, {
            chatInput,
            chatMessages,
        });
    }, [chatInput, chatMessages, selectedDocument]);

    const handleSendChatMessage = useCallback(async () => {
        const prompt = chatInput.trim();
        if (!prompt || !selectedDocument || isGeneratingHtml || !token) {
            return;
        }

        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: prompt,
        };

        setChatMessages(prev => [...prev, userMessage]);
        setChatInput('');
        setIsGeneratingHtml(true);
        setResultStatus('processing');

        try {
            const data: PortalDocumentGenerateResponse = await generatePortalDocumentResult(
                selectedDocument.id,
                commonHeaders,
                {
                    prompt,
                    mode: resultHtml ? 'refine' : 'regenerate',
                    includeAnalysisText: true,
                    baseVersion: resultVersion,
                    currentHtml: resultHtml || undefined,
                },
            );
            if (!isMountedRef.current) {
                return;
            }

            if (pollTimeoutRef.current) {
                window.clearTimeout(pollTimeoutRef.current);
            }
            pollGenerateJob(selectedDocument.id, data.jobId);
        } catch {
            if (isMountedRef.current) {
                setIsGeneratingHtml(false);
                setResultStatus('failed');
                message.error(intl.formatMessage({
                    id: 'document.processing.landing.chat.generateFailed',
                    defaultMessage: 'Failed to generate updated HTML.',
                }));
            }
        }
    }, [chatInput, commonHeaders, intl, isGeneratingHtml, pollGenerateJob, resultHtml, resultVersion, selectedDocument, token]);

    const columns: ColumnsType<LandingPageRow> = useMemo(() => [
        {
            title: intl.formatMessage({ id: 'document.processing.table.filename' }),
            key: 'name',
            width: 240,
            ellipsis: true,
            render: (_: unknown, row: LandingPageRow) => {
                const filename = row.source === 'portal'
                    ? filenameFromUrl(row.data.originalDocumentUrl)
                    : row.data.filename;

                return (
                    <Space style={{ minWidth: 0, maxWidth: '100%' }}>
                        <FileTextOutlined />
                        <Tooltip title={filename}>
                            <Button
                                type="link"
                                style={{
                                    padding: 0,
                                    maxWidth: 235,
                                    overflow: 'hidden',
                                }}
                                onClick={() => openRow(row)}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {filename}
                                </span>
                            </Button>
                        </Tooltip>
                    </Space>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'landing.documents.column.customer' }),
            key: 'customer',
            width: 200,
            render: (_: unknown, row: LandingPageRow) => row.source === 'portal' ? (
                <div>
                    <div style={{ fontWeight: 500 }}>{row.data.customer.fullName}</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{row.data.customer.email}</div>
                </div>
            ) : (
                <div style={{ fontWeight: 500 }}>{row.data.senderReceiver || '-'}</div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'landing.documents.column.caseNumber', defaultMessage: 'Vorgangsnummer' }),
            key: 'caseNumber',
            width: 140,
            render: (_: unknown, row: LandingPageRow) =>
                row.source === 'portal' ? (row.data.caseNumber || '-') : '-',
        },
        {
            title: intl.formatMessage({ id: 'guest.documents.column.type' }),
            key: 'queueType',
            width: 120,
            render: (_: unknown, row: LandingPageRow) => row.source === 'portal'
                ? intl.formatMessage({ id: `guest.documents.queueType.${row.data.queueType}`, defaultMessage: row.data.queueType })
                : intl.formatMessage({
                    id: 'document.processing.type.landing_page_assessment_report',
                    defaultMessage: row.data.type,
                }),
        },
        {
            title: intl.formatMessage({ id: 'guest.documents.column.status' }),
            key: 'status',
            width: 140,
            sorter: (a, b) => statusPriority(a) - statusPriority(b),
            defaultSortOrder: 'ascend',
            render: (_: unknown, row: LandingPageRow) => {
                const meta = ROW_STATUS_META[getRowStatusKey(row)];
                return (
                    <Tag color={meta.color}>
                        {intl.formatMessage({ id: meta.id, defaultMessage: meta.defaultMessage })}
                    </Tag>
                );
            },
        },
        {
            title: intl.formatMessage({ id: 'document.processing.table.uploadedAt' }),
            key: 'createdAt',
            width: 160,
            render: (_: unknown, row: LandingPageRow) => {
                const date = row.source === 'portal' ? row.data.createdAt : row.data.uploadedAt;
                return date
                    ? new Date(date).toLocaleString(undefined, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                    })
                    : '-';
            },
            sorter: (a, b) => {
                const dateOf = (row: LandingPageRow) => row.source === 'portal' ? row.data.createdAt : row.data.uploadedAt;
                return new Date(dateOf(a)).getTime() - new Date(dateOf(b)).getTime();
            },
        },
        {
            title: intl.formatMessage({ id: 'document.processing.table.actions' }),
            key: 'actions',
            width: 70,
            render: (_: unknown, row: LandingPageRow) => (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: 'view',
                                icon: <EyeOutlined />,
                                label: intl.formatMessage({
                                    id: 'guest.documents.viewOriginal',
                                    defaultMessage: 'Original',
                                }),
                                onClick: () => openRow(row),
                            },
                            ...(row.source === 'portal'
                                && row.data.assessmentReportStatus
                                && row.data.assessmentReportStatus !== 'completed' ? [{
                                key: 'resolve',
                                icon: <CheckCircleOutlined />,
                                label: intl.formatMessage({
                                    id: 'document.processing.landing.resolve.action',
                                    defaultMessage: 'Mark as resolved & notify customer',
                                }),
                                onClick: () => setDocumentPendingResolve(row),
                            }] : []),
                            ...(row.source === 'portal'
                                && (row.data.assessmentReportStatus === 'reply_received'
                                    || row.data.assessmentReportStatus === 'completed') ? [{
                                key: 'replies',
                                icon: <MailOutlined />,
                                label: intl.formatMessage({
                                    id: 'guest.documents.viewReplies',
                                    defaultMessage: 'Replies',
                                }),
                                onClick: () => openReplies(row.data),
                            }] : []),
                            {
                                key: 'delete',
                                icon: <DeleteOutlined />,
                                danger: true,
                                label: intl.formatMessage({
                                    id: 'document.processing.action.delete',
                                    defaultMessage: 'Delete',
                                }),
                                onClick: () => setDocumentPendingDelete(row),
                            },
                        ],
                    }}
                >
                    <Button
                        size="small"
                        onClick={event => event.preventDefault()}
                        aria-label={intl.formatMessage({
                            id: 'document.processing.table.actions',
                            defaultMessage: 'Actions',
                        })}
                    >
                        <MoreOutlined />
                    </Button>
                </Dropdown>
            ),
        },
    ], [intl, openRow, openReplies]);

    const htmlContent = selectedDocument ? resultHtml : null;
    const htmlResultUrl = selectedDocument?.analysisDocumentUrl || null;

    return (
        <>
            <TableSearch
                fields={[
                    {
                        label: <FormattedMessage id="document.processing.table.filename" />,
                        labelFor: 'filename',
                        renderer: (
                            <FormInput
                                id="filename"
                                value={filterOptions['filename']}
                                placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                                onChange={({ target }) => handleFilterChange({ [target.id]: target.value })}
                            />
                        ),
                    },
                    {
                        label: <FormattedMessage id="document.processing.table.uploadedAt" />,
                        labelFor: 'uploadedAt',
                        renderer: (
                            <FormDatePicker
                                id="uploadedAt"
                                onChange={date => handleFilterChange({
                                    uploadedAt: date ? moment(date).format('YYYY-MM-DD') : '',
                                })}
                                value={filterOptions['uploadedAt'] ? moment(filterOptions['uploadedAt']) : null}
                            />
                        ),
                    },
                    {
                        label: <FormattedMessage id="landing.documents.column.caseNumber" defaultMessage="Vorgangsnummer" />,
                        labelFor: 'caseNumber',
                        renderer: (
                            <FormInput
                                id="caseNumber"
                                value={filterOptions['caseNumber']}
                                placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                                onChange={({ target }) => handleFilterChange({ [target.id]: target.value })}
                            />
                        ),
                    },
                    {
                        label: <FormattedMessage id="document.processing.table.type" />,
                        labelFor: 'queueType',
                        renderer: (
                            <FormSelect
                                id="queueType"
                                value={filterOptions['queueType']}
                                placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                                onChange={value => handleFilterChange({ queueType: value })}
                                options={QUEUE_TYPE_OPTIONS.map(value => ({
                                    label: intl.formatMessage({ id: `guest.documents.queueType.${value}` }),
                                    value,
                                }))}
                                fullWidth
                            />
                        ),
                    },
                    {
                        label: <FormattedMessage id="document.processing.table.status" />,
                        labelFor: 'status',
                        renderer: (
                            <FormSelect
                                id="status"
                                value={filterOptions['status']}
                                placeholder={intl.formatMessage({ id: 'table.search.placeholder' })}
                                onChange={value => handleFilterChange({ status: value })}
                                options={STATUS_FILTER_OPTIONS.map(({ value, keys }) => ({
                                    label: intl.formatMessage({
                                        id: ROW_STATUS_META[keys[0]].id,
                                        defaultMessage: ROW_STATUS_META[keys[0]].defaultMessage,
                                    }),
                                    value,
                                }))}
                                fullWidth
                            />
                        ),
                    },
                ]}
                handleSubmit={handleFilterSubmit}
                handleReset={handleFilterReset}
                rowGutter={[16, 16]}
                colSizes={{ sm: 24, md: 12, lg: 8, xxl: 3 }}
            />

            <Table<LandingPageRow>
                dataSource={rows}
                columns={columns}
                rowKey="key"
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showSizeChanger: false,
                    showTotal: (t) => intl.formatMessage(
                        { id: 'landing.documents.pagination.total', defaultMessage: 'Total {total} documents' },
                        { total: t },
                    ),
                    onChange: (nextPage) => setPage(nextPage),
                }}
            />

            <Modal
                open={!!selectedDocument}
                title={selectedDocument ? filenameFromUrl(selectedDocument.originalDocumentUrl) : ''}
                onCancel={closeDocumentModal}
                footer={null}
                width="90vw"
                style={{ top: 20 }}
                bodyStyle={{ padding: 0, height: '80vh' }}
                destroyOnClose
            >
                {selectedDocument && (
                    <Tabs
                        defaultActiveKey="result"
                        destroyInactiveTabPane
                        style={{ height: '100%', padding: '0 16px 16px' }}
                        tabBarStyle={{ margin: '0 0 8px 8px' }}
                        items={[
                            {
                                key: 'original',
                                label: intl.formatMessage({
                                    id: 'document.processing.landing.tab.originalDocument',
                                    defaultMessage: 'Original document',
                                }),
                                children: (
                                    <div style={{ height: '72vh' }}>
                                        <PDFViewer
                                            pdfUrl={selectedDocument.originalDocumentUrl}
                                            filename={filenameFromUrl(selectedDocument.originalDocumentUrl)}
                                            hideControls
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'result',
                                label: selectedDocument.queueType === ASSESSMENT_REPORT_QUEUE_TYPE
                                    ? intl.formatMessage({ id: 'guest.documents.viewReplies', defaultMessage: 'Replies' })
                                    : intl.formatMessage({
                                        id: 'document.processing.landing.tab.resultHtml',
                                        defaultMessage: 'Result',
                                    }),
                                children: selectedDocument.queueType === ASSESSMENT_REPORT_QUEUE_TYPE ? (
                                    <div style={{ height: '72vh', padding: 16, overflow: 'auto', background: '#fafafa' }}>
                                        <AssessmentReportRepliesList
                                            loading={repliesLoading}
                                            error={repliesError}
                                            replies={replies}
                                            loadErrorMessage={intl.formatMessage({ id: 'guest.documents.replies.loadError', defaultMessage: "Couldn't load replies" })}
                                            emptyMessage={intl.formatMessage({ id: 'document.processing.landing.replies.staffEmpty', defaultMessage: 'The AI agent is extracting data from the document. Please wait and refresh in a few minutes.' })}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            height: '72vh',
                                            padding: 16,
                                            background: '#fafafa',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 16,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                type="primary"
                                                onClick={handleMarkResultReviewed}
                                                loading={isReviewingResult}
                                                disabled={reviewStatus === 'reviewed' || resultStatus !== 'ready'}
                                            >
                                                {reviewStatus === 'reviewed'
                                                    ? intl.formatMessage({
                                                        id: 'document.processing.landing.chat.reviewedDone',
                                                        defaultMessage: 'Marked as reviewed',
                                                    })
                                                    : intl.formatMessage({
                                                        id: 'document.processing.landing.chat.reviewedAction',
                                                        defaultMessage: 'Mark result as reviewed',
                                                    })}
                                            </Button>
                                        </div>

                                        <div
                                            style={{
                                                minHeight: 0,
                                                flex: 1,
                                                display: 'flex',
                                            }}
                                        >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                minHeight: 0,
                                                width: 320,
                                                flexShrink: 0,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 12,
                                                background: '#fff',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    padding: '18px 18px 12px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                }}
                                            >
                                                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                                                    {intl.formatMessage({
                                                        id: 'document.processing.landing.chat.title',
                                                        defaultMessage: 'Assistant',
                                                    })}
                                                </div>
                                                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                                                    {intl.formatMessage({
                                                        id: 'document.processing.landing.chat.description',
                                                        defaultMessage: 'Send a change request and update the preview.',
                                                    })}
                                                </div>
                                            </div>

                                            <div
                                                style={{
                                                    flex: 1,
                                                    overflowY: 'auto',
                                                    padding: 16,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 12,
                                                }}
                                            >
                                                {chatMessages.map(message => (
                                                    <div
                                                        key={message.id}
                                                        style={{
                                                            alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                                                            maxWidth: '92%',
                                                            padding: '12px 14px',
                                                            borderRadius: 14,
                                                            background: message.role === 'user' ? '#1d4ed8' : '#f3f4f6',
                                                            color: message.role === 'user' ? '#fff' : '#111827',
                                                            lineHeight: 1.5,
                                                            whiteSpace: 'pre-wrap',
                                                        }}
                                                    >
                                                        {message.content}
                                                    </div>
                                                ))}
                                                {isGeneratingHtml && (
                                                    <div
                                                        style={{
                                                            alignSelf: 'flex-start',
                                                            padding: '12px 14px',
                                                            borderRadius: 14,
                                                            background: '#f3f4f6',
                                                            color: '#111827',
                                                        }}
                                                    >
                                                        <Space size={10}>
                                                            <Spin size="small" />
                                                            <span>
                                                                {intl.formatMessage({
                                                                    id: 'document.processing.landing.chat.generating',
                                                                    defaultMessage: 'Applying changes...',
                                                                })}
                                                            </span>
                                                        </Space>
                                                    </div>
                                                )}
                                            </div>

                                            <div
                                                style={{
                                                    padding: 16,
                                                    borderTop: '1px solid #f0f0f0',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 12,
                                                }}
                                            >
                                                <TextArea
                                                    value={chatInput}
                                                    onChange={e => setChatInput(e.target.value)}
                                                    placeholder={intl.formatMessage({
                                                        id: 'document.processing.landing.chat.placeholder',
                                                        defaultMessage: 'Example: make the title shorter, add a summary section, and use a lighter background.',
                                                    })}
                                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                                    onPressEnter={e => {
                                                        if (!e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendChatMessage();
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    type="primary"
                                                    onClick={handleSendChatMessage}
                                                    loading={isGeneratingHtml}
                                                    disabled={!chatInput.trim() || isReviewingResult}
                                                >
                                                    {intl.formatMessage({
                                                        id: 'document.processing.landing.chat.send',
                                                        defaultMessage: 'Apply changes',
                                                    })}
                                                </Button>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                minWidth: 0,
                                                height: '100%',
                                                flex: 1,
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 12,
                                                background: '#fff',
                                                overflow: 'hidden',
                                                position: 'relative',
                                            }}
                                        >
                                            {isGeneratingHtml && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        zIndex: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'rgba(255, 255, 255, 0.78)',
                                                        backdropFilter: 'blur(2px)',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: 12,
                                                            padding: '24px 28px',
                                                            borderRadius: 16,
                                                            background: '#fff',
                                                            border: '1px solid #e5e7eb',
                                                            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.12)',
                                                        }}
                                                    >
                                                        <Spin size="large" />
                                                        <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
                                                            {intl.formatMessage({
                                                                id: 'document.processing.landing.chat.generating',
                                                                defaultMessage: 'Applying changes...',
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {htmlContent ? (
                                                <iframe
                                                    title={intl.formatMessage({
                                                        id: 'document.processing.landing.resultHtml.iframeTitle',
                                                        defaultMessage: 'Generated result',
                                                    })}
                                                    srcDoc={htmlContent}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        background: '#fff',
                                                    }}
                                                />
                                            ) : htmlResultUrl ? (
                                                <iframe
                                                    title={intl.formatMessage({
                                                        id: 'document.processing.landing.resultHtml.iframeTitle',
                                                        defaultMessage: 'Generated result',
                                                    })}
                                                    src={htmlResultUrl}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        background: '#fff',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: 24,
                                                        background:
                                                            'radial-gradient(circle at top left, #fff7ed 0%, #ffffff 45%, #f8fafc 100%)',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: 680,
                                                            padding: '32px 36px',
                                                            borderRadius: 18,
                                                            border: '1px solid #e5e7eb',
                                                            background: '#ffffff',
                                                            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 8,
                                                                padding: '6px 12px',
                                                                borderRadius: 999,
                                                                marginBottom: 18,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                letterSpacing: '0.04em',
                                                                textTransform: 'uppercase',
                                                                color: getResultPlaceholderTone(selectedDocument).badgeColor,
                                                                background: getResultPlaceholderTone(selectedDocument).badgeBackground,
                                                                border: `1px solid ${getResultPlaceholderTone(selectedDocument).badgeBorder}`,
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    background: getResultPlaceholderTone(selectedDocument).badgeColor,
                                                                }}
                                                            />
                                                            {intl.formatMessage({
                                                                id: 'document.processing.landing.resultHtml.pendingBadge',
                                                                defaultMessage: resultStatus === 'failed' ? 'Result failed' : 'Result in progress',
                                                            })}
                                                        </div>

                                                        <h2
                                                            style={{
                                                                margin: '0 0 12px',
                                                                fontSize: 28,
                                                                lineHeight: 1.2,
                                                                color: '#111827',
                                                            }}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'document.processing.landing.resultHtml.pendingTitle',
                                                                defaultMessage:
                                                                    resultStatus === 'failed'
                                                                        ? 'The result could not be generated.'
                                                                        : 'The result is still under review and processing.',
                                                            })}
                                                        </h2>

                                                        <p
                                                            style={{
                                                                margin: '0 0 22px',
                                                                fontSize: 15,
                                                                lineHeight: 1.7,
                                                                color: '#4b5563',
                                                            }}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'document.processing.landing.resultHtml.pendingDescription',
                                                                defaultMessage:
                                                                    resultStatus === 'failed'
                                                                        ? 'The HTML assistant did not return a valid result. Try sending a new instruction.'
                                                                        : 'The generated result document is not ready yet. Once the analysis is available, it will appear here for review.',
                                                            })}
                                                        </p>

                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                                gap: 14,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    padding: 16,
                                                                    borderRadius: 12,
                                                                    background: '#f8fafc',
                                                                    border: '1px solid #e5e7eb',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        marginBottom: 6,
                                                                        fontSize: 12,
                                                                        fontWeight: 700,
                                                                        letterSpacing: '0.05em',
                                                                        textTransform: 'uppercase',
                                                                        color: '#6b7280',
                                                                    }}
                                                                >
                                                                    {intl.formatMessage({
                                                                        id: 'document.processing.landing.resultHtml.statusLabel',
                                                                        defaultMessage: 'Current status',
                                                                    })}
                                                                </div>
                                                                <div style={{ fontSize: 15, color: '#111827' }}>
                                                                    {intl.formatMessage({
                                                                        id: `guest.documents.status.${getDisplayStatus(selectedDocument)}`,
                                                                    })}
                                                                </div>
                                                            </div>

                                                            <div
                                                                style={{
                                                                    padding: 16,
                                                                    borderRadius: 12,
                                                                    background: '#f8fafc',
                                                                    border: '1px solid #e5e7eb',
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        marginBottom: 6,
                                                                        fontSize: 12,
                                                                        fontWeight: 700,
                                                                        letterSpacing: '0.05em',
                                                                        textTransform: 'uppercase',
                                                                        color: '#6b7280',
                                                                    }}
                                                                >
                                                                    {intl.formatMessage({
                                                                        id: 'document.processing.landing.resultHtml.nextStepLabel',
                                                                        defaultMessage: 'Next step',
                                                                    })}
                                                                </div>
                                                                <div style={{ fontSize: 15, color: '#111827' }}>
                                                                    {intl.formatMessage({
                                                                        id: 'document.processing.landing.resultHtml.nextStepValue',
                                                                        defaultMessage: 'Wait for the generated result to be delivered here.',
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </Modal>

            <Modal
                title={intl.formatMessage({ id: 'document.processing.action.delete.document' })}
                open={!!documentPendingDelete}
                onOk={handleDeleteDocument}
                okText={intl.formatMessage({
                    id: 'document.processing.action.delete.document',
                    defaultMessage: 'Delete Document',
                })}
                okButtonProps={{ danger: true, loading: isDeletingDocument }}
                cancelButtonProps={{ disabled: isDeletingDocument }}
                onCancel={() => {
                    if (!isDeletingDocument) {
                        setDocumentPendingDelete(null);
                    }
                }}
                destroyOnClose
            >
                <p>{intl.formatMessage({ id: 'document.processing.delete.confirmation' })}</p>
            </Modal>

            <Modal
                title={intl.formatMessage({
                    id: 'document.processing.landing.resolve.action',
                    defaultMessage: 'Mark as resolved & notify customer',
                })}
                open={!!documentPendingResolve}
                onOk={handleResolveDocument}
                okText={intl.formatMessage({
                    id: 'document.processing.landing.resolve.confirm',
                    defaultMessage: 'Mark as resolved & notify customer',
                })}
                okButtonProps={{ loading: isResolvingDocument }}
                cancelButtonProps={{ disabled: isResolvingDocument }}
                onCancel={() => {
                    if (!isResolvingDocument) {
                        setDocumentPendingResolve(null);
                    }
                }}
                destroyOnClose
            >
                <p>{intl.formatMessage({
                    id: 'document.processing.landing.resolve.confirmation',
                    defaultMessage: 'This will immediately send an email to the customer letting them know the case is resolved. This cannot be undone. Continue?',
                })}</p>
            </Modal>

            <Modal
                open={!!repliesDocument}
                title={repliesDocument
                    ? `${filenameFromUrl(repliesDocument.originalDocumentUrl)}${repliesDocument.caseNumber
                        ? ` — ${intl.formatMessage({ id: 'document.processing.landing.caseNumberLabel', defaultMessage: 'Case {caseNumber}' }, { caseNumber: repliesDocument.caseNumber })}`
                        : ''}`
                    : intl.formatMessage({ id: 'guest.documents.replies.title', defaultMessage: 'Email replies' })}
                onCancel={closeReplies}
                footer={null}
                width="95vw"
                style={{ top: 10 }}
                bodyStyle={{ padding: 0, height: '90vh' }}
                destroyOnClose
            >
                {repliesDocument && (
                    <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ width: '65%', flexShrink: 0, borderRight: '1px solid #f0f0f0', padding: 16 }}>
                            <PDFViewer
                                pdfUrl={repliesDocument.originalDocumentUrl}
                                filename={filenameFromUrl(repliesDocument.originalDocumentUrl)}
                                hideControls
                            />
                        </div>
                        <div style={{ width: '35%', flexShrink: 0, minWidth: 0, padding: 16, overflowY: 'auto' }}>
                            <AssessmentReportRepliesList
                                loading={repliesLoading}
                                error={repliesError}
                                replies={replies}
                                loadErrorMessage={intl.formatMessage({ id: 'guest.documents.replies.loadError', defaultMessage: "Couldn't load replies" })}
                                emptyMessage={intl.formatMessage({ id: 'document.processing.landing.replies.staffEmpty', defaultMessage: 'The AI agent is extracting data from the document. Please wait and refresh in a few minutes.' })}
                            />
                        </div>
                    </div>
                )}
            </Modal>

        </>
    );
};

export default LandingPageDocumentsTab;
